/**
 * One copy table for the two note-capture background tasks (spec U-9):
 * `note-transcription` (speech-to-text on a voice note) and `note-ocr` (photo
 * text extraction into a note). Every string here is VERBATIM from the three
 * places this copy used to live separately: `utils/entityActivity.ts`'s
 * entity-glyph labels, `pages/NoteDetail/attachments/attachmentPresentation.ts`'s
 * row-tile copy, and the backend toast copy in
 * `packages/backend/src/socket/namespaces/notifications.namespace.ts` (for the
 * ready toast, the frontend's own `useNotificationSocket.tsx` OCR handler wins
 * instead, see `toast.ready['note-ocr']` below). Moving a string here never
 * changes it, a copy change is a separate PR.
 */

import { isTerminalOcrCode, isTerminalTranscriptCode } from '@bower/shared/schemas/upload.schema';
import { spreadsheetImportErrorMessage } from '@bower/shared/constants/spreadsheet-intake';
import {
  VIDEO_INSIGHT_ERROR_CODES,
  VIDEO_INSIGHT_MODES,
} from '@bower/shared/schemas/video-insight.schema';
import type {
  NoteOcrTerminalCode,
  NoteTranscriptTerminalCode,
  VideoInsightErrorCode,
  VideoInsightMode,
} from '@bower/shared';

export type CaptureTaskKind = 'note-transcription' | 'note-ocr';

interface ReadyToastCopy {
  readonly title: string;
  readonly description?: string;
  /** The toast's action button label, e.g. "Open". Capture kinds have none. */
  readonly actionLabel?: string;
}

interface FailedToastCopy {
  readonly title: string;
  readonly description: string;
  /** The toast's action button label, e.g. "View". Capture kinds have none. */
  readonly actionLabel?: string;
}

/**
 * One capture kind's copy table, parameterized by its terminal error-code
 * union.
 *
 * `label.byCode` stays a `Partial` record: not every terminal code has a
 * glyph label (`entityActivity.ts`'s `errorLabel` falls through to the
 * kind's generic error label for one that doesn't), the way
 * `TERMINAL_OCR_LABELS` used to. `tile.byCode` and `toast.failedByCode` are
 * full `Record`s: every terminal code gets its own row subtitle and toast,
 * the way `TERMINAL_TRANSCRIPTION_MESSAGES` / `TERMINAL_OCR_MESSAGES` and the
 * backend's `NO_RETRY_OCR_COPY` were typed. A missing key in either is a
 * compile error, not a silent fallback.
 */
interface CaptureTaskCopy<TerminalCode extends string> {
  readonly label: {
    readonly busy: string;
    readonly error: string;
    readonly byCode: Readonly<Partial<Record<TerminalCode, string>>>;
  };
  readonly tile: {
    readonly busy: string;
    readonly failed: string;
    readonly byCode: Readonly<Record<TerminalCode, string>>;
  };
  readonly toast: {
    readonly ready: ReadyToastCopy;
    readonly failed: FailedToastCopy;
    readonly failedByCode: Readonly<Record<TerminalCode, FailedToastCopy>>;
  };
}

/**
 * The entity glyph's label for each terminal code, shared with the tile
 * byCode below (same wording lead-in) the way `TERMINAL_TRANSCRIPT_LABELS`
 * used to be shared across the two modules. Exhaustive: every
 * `NoteTranscriptTerminalCode` gets a label, matching how
 * `TERMINAL_TRANSCRIPT_LABELS` was typed.
 */
const TRANSCRIPT_LABEL_BY_CODE = {
  NO_SPEECH_DETECTED: 'No speech found',
  TRANSCRIPT_REJECTED_IMPLAUSIBLE: 'No reliable transcript',
} satisfies Record<NoteTranscriptTerminalCode, string>;

/**
 * Deliberately partial, matching how `TERMINAL_OCR_LABELS` was typed:
 * `INVALID_FILE_TYPE` gets its own tile copy but no glyph label yet, and
 * falls through to the kind's generic error label.
 */
const OCR_LABEL_BY_CODE = {
  NO_TEXT_FOUND: 'No text found',
  AI_TEXT_EXTRACTION_QUOTA_EXHAUSTED: 'AI quota reached',
} satisfies Partial<Record<NoteOcrTerminalCode, string>>;

const TRANSCRIPTION_COPY = {
  label: {
    busy: 'Transcribing',
    error: 'Transcription failed',
    byCode: TRANSCRIPT_LABEL_BY_CODE,
  },
  tile: {
    busy: 'Transcribing… the transcript will appear in this note',
    failed: 'Transcription failed. The recording is saved; use Transcribe from the menu to retry',
    byCode: {
      NO_SPEECH_DETECTED: `${TRANSCRIPT_LABEL_BY_CODE.NO_SPEECH_DETECTED} in this recording. The recording is saved`,
      TRANSCRIPT_REJECTED_IMPLAUSIBLE: `${TRANSCRIPT_LABEL_BY_CODE.TRANSCRIPT_REJECTED_IMPLAUSIBLE} for this recording. The recording is saved`,
    },
  },
  toast: {
    ready: {
      title: 'Transcript ready',
      description: 'Your voice note has been transcribed.',
    },
    failed: {
      title: "Transcription didn't finish",
      description: 'Your recording is saved on the note. Transcription can be retried.',
    },
    failedByCode: {
      NO_SPEECH_DETECTED: {
        title: 'No speech in this recording',
        description: "We didn't find any speech to transcribe. The recording is saved on the note.",
      },
      TRANSCRIPT_REJECTED_IMPLAUSIBLE: {
        title: 'No transcript for this recording',
        description:
          "We couldn't produce a reliable transcript for this audio. The recording is saved on the note.",
      },
    },
  },
} satisfies CaptureTaskCopy<NoteTranscriptTerminalCode>;

const OCR_COPY = {
  label: {
    busy: 'Reading text',
    error: "Couldn't read text",
    byCode: OCR_LABEL_BY_CODE,
  },
  tile: {
    busy: 'Reading text… it will appear in this note',
    failed: "Couldn't read text. The image is saved; use Image to text from the menu to retry",
    byCode: {
      NO_TEXT_FOUND: 'No text found in this image. The image is saved',
      AI_TEXT_EXTRACTION_QUOTA_EXHAUSTED:
        'AI quota reached. The image is saved; after you upgrade, use Image to text from the menu',
      INVALID_FILE_TYPE: "This file can't be read as text. The image is saved",
      IMAGE_UNDECODABLE: "This image couldn't be opened. The file may be damaged",
      IMAGE_TOO_LARGE: 'This image is too large to read as text. The image is saved',
    },
  },
  toast: {
    // The frontend's own OCR-ready handler (`useNotificationSocket.tsx`)
    // ignores the backend's title/message and shows this instead, what
    // users see today. Warnings copy stays in `utils/ocrWarningCopy.ts`.
    ready: {
      title: 'Text added to the note',
    },
    failed: {
      title: "Text extraction didn't finish",
      description: 'Your image is saved on the note. Text extraction can be retried.',
    },
    failedByCode: {
      NO_TEXT_FOUND: {
        title: 'No text in this image',
        description: "We didn't find any text to add. Your image is saved on the note.",
      },
      AI_TEXT_EXTRACTION_QUOTA_EXHAUSTED: {
        title: 'Text extraction limit reached',
        description:
          'Your workspace has used its text extraction allowance for this period. Your image is saved on the note.',
      },
      INVALID_FILE_TYPE: {
        title: "This file can't be read as text",
        description: 'We can only extract text from images. Your image is saved on the note.',
      },
      IMAGE_UNDECODABLE: {
        title: "This image couldn't be opened",
        description: 'The file may be damaged. Try adding the image to the note again.',
      },
      IMAGE_TOO_LARGE: {
        title: 'This image is too large to read',
        description:
          'Your image is saved on the note. To extract its text, add a smaller copy, for example a JPEG.',
      },
    },
  },
} satisfies CaptureTaskCopy<NoteOcrTerminalCode>;

interface TaskCopyMap {
  readonly 'note-transcription': CaptureTaskCopy<NoteTranscriptTerminalCode>;
  readonly 'note-ocr': CaptureTaskCopy<NoteOcrTerminalCode>;
}

const TASK_COPY: TaskCopyMap = {
  'note-transcription': TRANSCRIPTION_COPY,
  'note-ocr': OCR_COPY,
};

/**
 * `note-ocr`'s failed-append toast: the note itself refused the extracted
 * text (error code `APPEND_<code>`), so a retry cannot promise a different
 * answer either. Kept out of `failedByCode` because it is matched by PREFIX,
 * not by an exact code: see `failureToast` below.
 */
const OCR_APPEND_FAILED_TOAST: FailedToastCopy = {
  title: "Text couldn't be added to the note",
  description: "We read your image but couldn't add its text to the note.",
};

/** Read-only export for callers that need a kind's full copy (labels, tile text). */
export const CAPTURE_TASK_COPY: Readonly<TaskCopyMap> = TASK_COPY;

/** The ready toast for a capture kind, as shown today (see `toast.ready` above). */
export function readyToast(kind: CaptureTaskKind): ReadyToastCopy {
  return CAPTURE_TASK_COPY[kind].toast.ready;
}

/**
 * The exact-code toast lookup, narrowed per kind so an arbitrary `string`
 * code (an unmapped failure, an `APPEND_` prefix) never has to be cast
 * through either kind's exhaustive `failedByCode` record.
 */
function failedByCode(kind: CaptureTaskKind, code: string): FailedToastCopy | undefined {
  if (kind === 'note-transcription') {
    return isTerminalTranscriptCode(code)
      ? CAPTURE_TASK_COPY['note-transcription'].toast.failedByCode[code]
      : undefined;
  }
  return isTerminalOcrCode(code)
    ? CAPTURE_TASK_COPY['note-ocr'].toast.failedByCode[code]
    : undefined;
}

/**
 * The failure toast for a capture kind and its (optional) error code: an
 * exact-code match wins, then (OCR only) the `APPEND_` prefix rule, then the
 * kind's default. Transcription mirrors the backend's
 * `noteTranscriptFailureCopy` precedence exactly.
 */
export function failureToast(kind: CaptureTaskKind, code?: string): FailedToastCopy {
  const copy = CAPTURE_TASK_COPY[kind];
  if (code !== undefined) {
    const byCode = failedByCode(kind, code);
    if (byCode) return byCode;
    if (kind === 'note-ocr' && code.startsWith('APPEND_')) {
      return OCR_APPEND_FAILED_TOAST;
    }
  }
  return copy.toast.failed;
}

/**
 * The three bulk background-task kinds (spec "3b, decided 2026-10-02"):
 * `content-extraction` (a file being read), `note-from-file` (a file being
 * converted to a note) and `spreadsheet-import`. None has a typed terminal-code
 * union the way the capture kinds do (no `isTerminalXCode` guard exists for
 * them yet), so there is no `byCode` here; `spreadsheet-import`'s failure
 * toast instead reuses `spreadsheetImportErrorMessage(code)` directly as its
 * only text (what the bespoke `job:error` toast showed: one message, no
 * title above it), rather than duplicating its strings. A kind with no ready toast
 * (`content-extraction`, whose success is silent) simply has none: `ready` is
 * optional, unlike the capture kinds' required one.
 */
export type BulkTaskKind = 'content-extraction' | 'note-from-file' | 'spreadsheet-import';

/** A bulk failure toast: `spreadsheet-import`'s is one message, so no description. */
interface BulkFailedToastCopy {
  readonly title: string;
  readonly description?: string;
  readonly actionLabel?: string;
}

interface BulkTaskCopy {
  readonly label: Readonly<{ busy: string; error: string }>;
  readonly toast: {
    readonly ready?: ReadyToastCopy;
    readonly failed: (code?: string) => BulkFailedToastCopy;
  };
}

const CONTENT_EXTRACTION_COPY: BulkTaskCopy = {
  label: {
    busy: 'Reading file',
    error: "Couldn't read file",
  },
  toast: {
    // Success is silent (spec: a file being read shows no ready toast).
    failed: () => ({
      title: "Couldn't read this file",
      description: 'The file is saved. Its text could not be extracted.',
    }),
  },
};

const NOTE_FROM_FILE_COPY: BulkTaskCopy = {
  label: {
    busy: 'Converting to note',
    error: "Couldn't convert to a note",
  },
  toast: {
    ready: {
      title: 'File converted to a note',
      actionLabel: 'View note',
    },
    failed: () => ({
      title: "Couldn't convert the file to a note",
      description: 'The file is saved where you uploaded it.',
    }),
  },
};

const SPREADSHEET_IMPORT_COPY: BulkTaskCopy = {
  label: {
    busy: 'Setting up spreadsheet',
    error: 'Spreadsheet import failed',
  },
  toast: {
    ready: {
      title: 'Your spreadsheet is ready',
      actionLabel: 'Open',
    },
    failed: (code) => ({
      title: spreadsheetImportErrorMessage(code),
      actionLabel: 'View',
    }),
  },
};

const BULK_TASK_COPY: Readonly<Record<BulkTaskKind, BulkTaskCopy>> = {
  'content-extraction': CONTENT_EXTRACTION_COPY,
  'note-from-file': NOTE_FROM_FILE_COPY,
  'spreadsheet-import': SPREADSHEET_IMPORT_COPY,
};

/** The ready toast for a bulk kind, or `undefined` for one whose success is silent. */
export function bulkReadyToast(kind: BulkTaskKind): ReadyToastCopy | undefined {
  return BULK_TASK_COPY[kind].toast.ready;
}

/** The failure toast for a bulk kind and its (optional) error code. */
export function bulkFailureToast(kind: BulkTaskKind, code?: string): BulkFailedToastCopy {
  return BULK_TASK_COPY[kind].toast.failed(code);
}

/**
 * The `video-insight` kind's toasts (spec "3c, decided 2026-10-03"): the same
 * text the bespoke `job:complete` / `job:error` toasts show today. The title
 * is the backend's `${JOB_NAME} Complete` / `${JOB_NAME} Failed`
 * (`video-insight.delivery.ts`, `notifications.namespace.ts`); the lines are
 * VERBATIM from `packages/backend/src/services/video-insights/video-insight.copy.ts`.
 * That module still serves the legacy `job:*` path (a job queued without
 * `announce: 'task'`), so the two copies live side by side until that path
 * goes.
 */
const VIDEO_INSIGHT_READY_TITLE = 'Video insight Complete';
const VIDEO_INSIGHT_FAILED_TITLE = 'Video insight Failed';

/** Exhaustive: a new mode fails to compile here instead of toasting nothing. */
const VIDEO_INSIGHT_READY_LINE = {
  chapters: 'Proposed step markers are ready to review.',
  draft_protocol: 'A draft protocol was created from the video.',
  enrich: 'Enrichment notes are ready to review.',
  deviations: 'The deviation check is ready to review.',
} satisfies Record<VideoInsightMode, string>;

/**
 * Deliberately PARTIAL, as the backend's `USER_MESSAGES` is: the codes with no
 * entry take `VIDEO_INSIGHT_GENERIC_FAILURE`, and so does a missing code.
 */
const VIDEO_INSIGHT_FAILURE_LINE: Readonly<Partial<Record<VideoInsightErrorCode, string>>> = {
  FEATURE_DISABLED: 'We could not confirm video insights are enabled for your account.',
  QUOTA_EXHAUSTED:
    "You've used all of this period's AI extractions. The video was not read; try again next period or upgrade.",
  VIDEO_NOT_READY: 'The video is still processing. Try again once it is playable.',
  NOT_A_VIDEO: 'That file is not a video.',
  CONTEXT_NOT_FOUND: 'The protocol could not be found.',
  CONTEXT_TYPE_UNSUPPORTED: 'Video insights can only be read against a protocol for now.',
  CONTEXT_HAS_NO_STEPS: 'The protocol has no steps to read the video against.',
  GCS_REQUIRED:
    'This environment cannot read videos for AI analysis: it has no cloud storage configured.',
  EMPTY_RESPONSE_EXHAUSTED:
    'The model returned nothing after several tries. Please try again later.',
  MAX_TOKENS:
    'The model ran out of output budget before it finished answering. This video is likely longer or busier than the model can summarize in one pass.',
  DELIVERY_ATTEMPTS_EXHAUSTED: "We couldn't finish reading this video after several attempts.",
  REQUESTER_INACTIVE:
    'The person who requested this is no longer an active member of this workspace.',
  DRAFT_SOURCE_RESTRICTED:
    'This video has restricted access, and a drafted protocol could not carry that restriction. Move the video to a collection, or draft the protocol yourself.',
  CONTEXT_SOURCE_RESTRICTED:
    'This protocol has restricted access and the video does not, so reading them together would put protocol content somewhere more people can open it. Restrict the video the same way, or move it under the protocol.',
};
const VIDEO_INSIGHT_GENERIC_FAILURE = 'Reading the video failed. Please try again.';

export function isVideoInsightMode(value: string): value is VideoInsightMode {
  return (VIDEO_INSIGHT_MODES as readonly string[]).includes(value);
}

function isVideoInsightErrorCode(value: string): value is VideoInsightErrorCode {
  return (VIDEO_INSIGHT_ERROR_CODES as readonly string[]).includes(value);
}

/**
 * The ready toast for one mode. `null` (a mode this build does not know, from
 * a newer backend) still gets the title, without a line it would have to
 * invent.
 */
export function videoInsightReadyToast(mode: VideoInsightMode | null): {
  readonly title: string;
  readonly description?: string;
} {
  if (!mode) return { title: VIDEO_INSIGHT_READY_TITLE };
  return { title: VIDEO_INSIGHT_READY_TITLE, description: VIDEO_INSIGHT_READY_LINE[mode] };
}

/** The failure toast for an (optional) error code; an unmapped or missing code takes the generic line. */
export function videoInsightFailureToast(code?: string): {
  readonly title: string;
  readonly description: string;
} {
  // Through the closed enum, not a bare index: a code like `constructor` must
  // not read off the object's prototype.
  const line = code && isVideoInsightErrorCode(code) ? VIDEO_INSIGHT_FAILURE_LINE[code] : undefined;
  return { title: VIDEO_INSIGHT_FAILED_TITLE, description: line ?? VIDEO_INSIGHT_GENERIC_FAILURE };
}

/** Every known task kind's entity-glyph label, read by `entityActivity.ts`. */
export const TASK_LABELS: Readonly<
  Record<CaptureTaskKind | BulkTaskKind | 'video-insight', { busy: string; error: string }>
> = {
  'note-transcription': CAPTURE_TASK_COPY['note-transcription'].label,
  'note-ocr': CAPTURE_TASK_COPY['note-ocr'].label,
  'content-extraction': CONTENT_EXTRACTION_COPY.label,
  'note-from-file': NOTE_FROM_FILE_COPY.label,
  'spreadsheet-import': SPREADSHEET_IMPORT_COPY.label,
  // No slot is written for this kind (its state is metadata.videoInsights), so
  // no glyph reads it yet. Verbatim from the panel: the button's loadingText
  // (ProtocolVideoInsightsPanel.tsx) and the row's fallback failure line
  // (ProtocolVideoInsightViews.tsx).
  'video-insight': { busy: 'Reading the video', error: 'Reading the video failed.' },
};
